/*=========================================================================================
  File Name: module-usertax-actions.js
  Description: UserTax Module Actions
  ----------------------------------------------------------------------------------------
  Project: MISA MTAX WebApp
  Author: MISA JSC
  Author URL: http://www.misa.com.vn
==========================================================================================*/
import httpClient from '../../api/axios-httpclient';
import InvoiceAPI from '@/api/InvoiceAPI.js';
import DownloadAPI from '@/api/DownloadAPI.js';
import CertificateAPI from '@/api/CertificateAPI.js';
import DashboardAPI from '@/api/DashboardAPI.js';
import CertificateTrialAPI from '@/api/CertificateTrialAPI.js';
import { NumberRetry, NumberTimeOutRetry } from '@/api/base/base-api';
import commonFunc from '@/common/commonFunction';
import EventBusGlobal, { GlobalEventName } from '@/sys/eventBusGlobal';
import { MSEnum } from '../../common/enumeration';
import i18n from '@/i18n/i18n';

export default {
	loadInvoices: async function(context, payload) {
		let isShowLoading = payload && payload.isLoading;
		let res = await httpClient.getAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/v2',
				params: payload
			},
			isShowLoading
		);
		if (res && res.Success) {
			context.commit('updateInvoices', { data: res.Data, isGetTotal: payload.IsGetTotal });
		}
		return null;
	},

	/**
	 * Đầu gọi lấy danh sách hóa đơn bên dự liệu elastic
	 */
	loadInvoicesElastic: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/v3`,
				data: payload
			},
			true
		);
		if (res && res.Success) {
			context.commit('updateInvoices', { data: res.Data, isGetTotal: payload.IsGetTotal });
		}
		return null;
	},

	loadCertificate: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: CertificateAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${payload}`
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	getInvoicesById: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${payload}`
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Ghi log việc người dùng thực hiện click vào xem ở chế độ nâng cao
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	viewInvoiceDetailByJobType: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/log/${payload.InvoiceId}/${payload.JobType}`
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	getInvoicePdf: async function(context, payload) {
		//Không có PDF
		if (!payload.IsHasPdfClient) return null;

		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.InvoiceId}/pdf`,
			responseType: 'arraybuffer'
		});
		if (res && res instanceof ArrayBuffer) {
			return res;
		}
		return null;
	},

	getInvoiceImage: async function(context, payload) {
		//Không có Image
		if (!payload.IsHasImage) return null;

		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.InvoiceId}/image`,
			responseType: 'arraybuffer'
		});
		if (res && res instanceof ArrayBuffer) {
			return res;
		}
		return null;
	},

	/**
	 * Lấy file hóa đơn (PDF hoặc Image) qua 1 request duy nhất.
	 * Backend tự quyết định trả PDF (ưu tiên) hoặc Image (fallback).
	 * @param {*} context
	 * @param {*} payload - Hóa đơn cần lấy file, có InvoiceId
	 * @returns {ArrayBuffer} - Nội dung file (PDF hoặc Image), null nếu không có file
	 */
	getInvoiceFile: async function(context, payload) {
		//Không có file PDF và không có file Image thì không gọi API
		if (!payload.IsHasPdfClient && !payload.IsHasImage) return null;

		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.InvoiceId}/file`,
			responseType: 'arraybuffer'
		});
		if (res && res instanceof ArrayBuffer) {
			return res;
		}
		return null;
	},

	getInvoiceXml: async function(context, payload) {
		//Không có XML
		if (!payload.IsHasXml) return null;

		let nameFile = '';
		if (payload.InvoiceNo && payload.SellerTaxCode) {
			nameFile = `${payload.SellerTaxCode}_${payload.InvoiceNo}`;
		} else {
			nameFile = payload.IsCheckThirdParty ? 'hoa_don' : 'hoa_don_dau_vao';
		}
		let ProgressData = {
			percentCompleted: 0,
			fileName: `${nameFile}.xml`,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.getAsync({
			url: `${DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.InvoiceId}/xml`,
			responseType: 'arraybuffer',
			onDownloadProgress: function(progressEvent) {
				var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				if (progressEvent.loaded > progressEvent.total) {
					percentCompleted = 100;
				}
				ProgressData = {
					percentCompleted: percentCompleted,
					fileName: `${nameFile}.xml`,
					Success: true
				};
				EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			}
		});
		if (res) {
			if (res instanceof ArrayBuffer) {
				commonFunc.downloadByBlob(res, 'application/xml', `${nameFile}.xml`);
			} else {
				ProgressData = {
					percentCompleted: 0,
					fileName: `${nameFile}.xml`,
					Success: false
				};
				EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			}
			return res;
		}
		return null;
	},

	getInvoiceZip: async function(context, payload) {
		let fileName = commonFunc.getNameFileDownload(payload.fileName, payload, 'zip');
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/zip`,
			data: payload
		});
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download`,
					responseType: 'arraybuffer',
					onDownloadProgress: function(progressEvent) {
						if (progressEvent.total) {
							var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							ProgressData = {
								percentCompleted: percentCompleted,
								fileName: fileName,
								Success: true
							};
							EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						}
					}
				});
				if (downloaded instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
					commonFunc.downloadByBlob(downloaded, 'application/zip', fileName);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: commonFunc.getNameFileDownload(null, payload, 'zip'),
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			return null;
		}
	},

	/**
	 * Danh sách file
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	getFileZipSelectType: async function(context, payload) {
		let extensionFile = 'zip';
		let fileName = commonFunc.getNameFileDownload(payload.fileName, payload, extensionFile);
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/zip`,
			data: payload
		});
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download`,
					responseType: 'arraybuffer'
				});
				if (downloaded instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
					commonFunc.downloadByBlob(downloaded, 'application/zip', fileName);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: commonFunc.getNameFileDownload(null, payload, 'zip'),
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			return null;
		}
	},

	/**
	 * Api khi tải số lượng hóa đơn lớn
	 * @param {*} context
	 * @param {*} payload
	 */
	getLargeNumberFileZip: async function(context, payload) {
		let extensionFile = 'zip';
		let fileName = commonFunc.getNameFileDownload(payload.fileName, payload, extensionFile);
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/v2/zip`,
			data: payload
		});
		return res;
	},

	getInvoiceZipByFilter: async function(context, payload) {
		let fileName = commonFunc.getNameFileDownload(payload.fileName, payload, 'zip');
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/zip`,
			data: payload
		});
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let fileNameDefault = commonFunc.getNameFileDownload(null, payload, 'zip');
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download`,
					responseType: 'arraybuffer',
					onDownloadProgress: function(progressEvent) {
						if (progressEvent.total) {
							var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							ProgressData = {
								percentCompleted: percentCompleted,
								fileName: fileNameDefault,
								Success: true
							};
							EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						}
					}
				});
				if (downloaded instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileNameDefault,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
					commonFunc.downloadByBlob(downloaded, 'application/zip', fileNameDefault);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileNameDefault,
							Success: false
						};
						EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: commonFunc.getNameFileDownload(null, payload, 'zip'),
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			return null;
		}
	},

	exportInvoices: async function(context, payload) {
		let fileName = 'excel.xlsx';
		switch (payload.Type) {
			case MSEnum.systemImport.MisaService:
				fileName = 'Misa_dich_vu.xlsx';
				break;
			case MSEnum.systemImport.MisaMerchandise:
				fileName = 'Misa_hang_hoa.xlsx';
				break;
			case MSEnum.systemImport.FastService:
				fileName = 'Fast_dich_vu.xlsx';
				break;
			case MSEnum.systemImport.FastMerchandise:
				fileName = 'Fast_hang_hoa.xlsx';
				break;
			case MSEnum.systemImport.SmartProService:
				fileName = 'SmartPro_dich_vu.xlsx';
				break;
			case MSEnum.systemImport.SmartProMerchandise:
				fileName = 'SmartPro_hang_hoa.xlsx';
				break;
			case MSEnum.lstImportVal.InputInvoice:
				fileName = 'DS_hoa_don_dau_vao.xlsx';
				break;
			case MSEnum.lstInvoiceStatement.ListInvoiceBuyer:
				fileName = `Bang_ke_hoa_don_hang_hoa_dich_vu_mua_vao_tong_hop.xlsx`;
				break;
			case MSEnum.InvoiceReportList.ListInvoiceBuyer802021:
				fileName = `Bang ke mua vao Mau 01-1/HT (Thong tu 80/2021/TT-BTC).xlsx`;
				break;
			case MSEnum.lstInvoiceStatement.InvoiceBuyer:
				fileName = `Bang_ke_hoa_don_hang_hoa_dich_vu_mua_vao_chi_tiet.xlsx`;
				break;
			case MSEnum.outInvoiceExport.InvoiceSaleAmis:
				fileName = `MISA_AMIS_Ke_toan_Hang_hoa.xlsx`;
				break;
			case MSEnum.outInvoiceExport.ServiceAmis:
				fileName = `MISA_AMIS_Ke_toan_Dich_vu.xlsx`;
				break;
			case MSEnum.outInvoiceExport.InvoiceListAmis:
				fileName = `MISA_AMIS_Ke_toan_Danh_sach_hoa_don.xlsx`;
				break;
			case MSEnum.outInvoiceExport.SMEInvoiceSale:
				fileName = `MISA_SME_Hoa_don_ban_hang.xlsx`;
				break;
			case MSEnum.outInvoiceExport.SMESale:
				fileName = `MISA_SME_Ban_hang.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceServiceMOKA:
				fileName = `MOKA_Hoa_don_mua_dich_vu.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceSaleMOKA:
				fileName = `MOKA_Hoa_don_mua_hang_trong_nuoc.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceMOKA:
				fileName = `MOKA_Phieu_chi_tien_mat.xlsx`;
				break;
			case MSEnum.invoicesMassan.InvoiceMassan:
				fileName = `MASAN_ Bang_Ke_HHDV_mua_vao.xlsx`;
				break;
		}
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/export',
			data: payload
		});
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let exportFile = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/export`,
					responseType: 'arraybuffer',
					onDownloadProgress: function(progressEvent) {
						if (progressEvent.total) {
							var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							ProgressData = {
								percentCompleted: percentCompleted,
								fileName: fileName,
								Success: true
							};
							EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						}
					}
				});
				if (exportFile instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
					commonFunc.downloadByBlob(exportFile, 'application/vnd.ms-excel', fileName);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: fileName,
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			return null;
		}
	},

	exportInvoicesByFilter: async function(context, payload) {
		let fileName = 'excel.xlsx';
		let dateNow = new Date();
		let dateRange =
			(dateNow.getDate() < 10 ? '0' + dateNow.getDate() : dateNow.getDate()) +
			'.' +
			(dateNow.getMonth() < 9 ? '0' + (dateNow.getMonth() + 1) : dateNow.getMonth() + 1) +
			'.' +
			dateNow.getFullYear();
		switch (payload.Type) {
			case MSEnum.systemImport.MisaService:
				fileName = `Misa_dich_vu_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.MisaMerchandise:
				fileName = `Misa_hang_hoa_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.FastService:
				fileName = `Fast_dich_vu_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.FastMerchandise:
				fileName = `Fast_hang_hoa_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.SmartProService:
				fileName = `SmartPro_dich_vu_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.SmartProMerchandise:
				fileName = `SmartPro_hang_hoa_${dateRange}.xlsx`;
				break;
			case MSEnum.systemImport.SME2012Service:
				fileName = `Mau_chung_tu_mua_dich_vu.xlsx`;
				break;
			case MSEnum.systemImport.SME2012PayMerchandise:
				fileName = `Mau_chung_tu_mua_hang_qua_kho_thanh_toan_ngay.xlsx`;
				break;
			case MSEnum.systemImport.SME2012NotPayMerchandise:
				fileName = `Mau_chung_tu_mua_hang.xlsx`;
				break;
			case MSEnum.lstImportVal.InputInvoice:
				fileName = payload.IsCheckThirdParty
					? `DS_hoa_don_${dateRange}.xlsx`
					: `DS_hoa_don_dau_vao_${dateRange}.xlsx`;
				break;
			case MSEnum.lstImportOutInvoiceVal.OutInvoiceList:
				fileName = `DS_hoa_don_dau_ra_${dateRange}.xlsx`;
				break;
			case MSEnum.lstInvoiceStatement.ListInvoiceBuyer:
				fileName = payload.IsCheckThirdParty
					? `Bang_ke_hoa_don_hang_hoa_dich_vu_tong_hop.xlsx`
					: `Bang_ke_hoa_don_hang_hoa_dich_vu_mua_vao_tong_hop.xlsx`;
				break;
			case MSEnum.InvoiceReportList.ListInvoiceBuyer802021:
				fileName = `Bang ke mua vao Mau 01-1/HT (Thong tu 80/2021/TT-BTC).xlsx`;
				break;
			case MSEnum.lstInvoiceStatement.InvoiceBuyer:
				fileName = payload.IsCheckThirdParty
					? `Bang_ke_hoa_don_hang_hoa_dich_vu_chi_tiet.xlsx`
					: `Bang_ke_hoa_don_hang_hoa_dich_vu_mua_vao_chi_tiet.xlsx`;
				break;
			case MSEnum.outInvoiceExport.InvoiceSaleAmis:
				fileName = `MISA_AMIS_Ke_toan_Hang_hoa_${dateRange}.xlsx`;
				break;
			case MSEnum.outInvoiceExport.ServiceAmis:
				fileName = `MISA_AMIS_Ke_toan_Dich_vu_${dateRange}.xlsx`;
				break;
			case MSEnum.outInvoiceExport.InvoiceListAmis:
				fileName = `MISA_AMIS_Ke_toan_Danh_sach_hoa_don_${dateRange}.xlsx`;
				break;
			case MSEnum.outInvoiceExport.SMESale:
				fileName = `MISA_SME_Ban_hang_${dateRange}.xlsx`;
				break;
			case MSEnum.outInvoiceExport.SMEInvoiceSale:
				fileName = `MISA_SME_Hoa_don_ban_hang_${dateRange}.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceServiceMOKA:
				fileName = `MOKA_Hoa_don_mua_dich_vu_${dateRange}.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceSaleMOKA:
				fileName = `MOKA_Hoa_don_mua_hang_trong_nuoc_${dateRange}.xlsx`;
				break;
			case MSEnum.newInvoiceExport.InvoiceMOKA:
				fileName = `MOKA_Phieu_chi_tien_mat_${dateRange}.xlsx`;
				break;
			case MSEnum.invoicesMassan.InvoiceMassan:
				fileName = `MASAN_ Bang_Ke_HHDV_mua_vao_${dateRange}.xlsx`;
				break;
			case MSEnum.ConfirmGoodsVatRate.ConfirmGoodsVatRateList:
				fileName = `Ket_qua_kiem_tra_thue_suat.xlsx`;
				break;
			case MSEnum.DeclarationExport.DeclarationList:
				fileName = `DS_To_khai_hai_quan_${dateRange}.xlsx`;
				break;
			case MSEnum.DeclarationExport.DeclarationDetail:
				fileName = `Chi_tiet_to_khai_hai_quan_${dateRange}.xlsx`;
				break;
			case MSEnum.DeclarationExportAccounting.MisaSmeMerchandise:
				fileName = `Mua_hang_qua_kho.xlsx`;
				break;
		}
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/exportparams',
			data: payload
		});
		let numberRetry = payload.NumberRetry > 0 ? payload.NumberRetry : NumberRetry;
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let exportFile = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/export`,
					responseType: 'arraybuffer',
					onDownloadProgress: function(progressEvent) {
						if (progressEvent.total) {
							var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							ProgressData = {
								percentCompleted: percentCompleted,
								fileName: fileName,
								Success: true
							};
							EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						}
					}
				});
				if (exportFile instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
					commonFunc.downloadByBlob(exportFile, 'application/vnd.ms-excel', fileName, payload.exportContext);
				} else {
					if (tryTime >= numberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: fileName,
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			return null;
		}
	},

	/**
	 * Kiểm tra lại 1 hóa đơn
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	checkAgain: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${payload}/checkagain`,
				timeout: window.__env.appSetting.timeoutCheckAgain
			},
			true
		);
		return res;
	},

	/**
	 * Kiểm tra lại hàng loạt hóa đơn
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	checkAgainV2: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/checkagainv2`,
				data: payload,
				timeout: window.__env.appSetting.timeoutCheckAgain
			},
			true
		);
		return res;
	},

	// Chấp nhận địa chỉ, tên trên hóa đơn
	updateCheckingResult: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/checkingresult`,
			data: payload
		});
		return res;
	},

	// Chấp nhận tồn tại trên CQT
	acceptExistTaxAuthorityAsync: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/accept-exist-tax`,
			data: payload
		});
		return res;
	},

	// Chấp nhận thông tin khác trên hóa đơn
	updateCheckingOther: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/acceptother`,
			data: payload
		});
		return res;
	},

	// Cập nhật trạng thái hóa đơn
	invoiceAccept: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/invoiceaccept`,
			params: payload
		});
		return res;
	},

	// Đưa hóa đơn vào Hóa đơn đã xóa or khôi phục lại
	invoiceDelete: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/trash`,
				params: payload
			},
			true
		);
		return res;
	},

	// KHôi phục HĐ đã xóa
	restoreInvoicesAsync: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/restore`,
				data: payload
			},
			true
		);
		return res;
	},

	// Đưa danh sách hóa đơn vào Hóa đơn đã xóa
	invoiceDeletes: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/trashs`,
				data: payload
			},
			true
		);
		return res;
	},

	updateStickInList: async function(context, payload) {
		context.commit('updateStickInList', payload);
	},
	// Thực hiện bổ sung ghi chú cho hóa đơn
	addNoted: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/noted`,
				data: payload
			},
			true
		);
		return res;
	},
	/// report mail
	reportMail: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/reportmail`,
				data: payload
			},
			true
		);
		return res;
	},
	// Thực hiện tải file attach
	downloadAttach: async function(context, payload) {
		let nameFile = '';
		let ProgressData = {
			percentCompleted: 0,
			fileName: payload.File.FileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.getAsync(
			{
				url: `${DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.File.FileId}/${
					payload.InvoiceId
				}/attachfile`,
				responseType: 'arraybuffer',
				onDownloadProgress: function(progressEvent) {
					var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					if (progressEvent.loaded > progressEvent.total) {
						percentCompleted = 100;
					}
					ProgressData = {
						percentCompleted: percentCompleted,
						fileName: payload.File.FileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
				}
			},
			true
		);
		if (res) {
			if (res instanceof ArrayBuffer) {
				commonFunc.downloadByBlob(res, payload.File.FileContentType, payload.File.FileName);
			} else {
				ProgressData = {
					percentCompleted: 0,
					fileName: payload.File.FileName,
					Success: false
				};
				EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			}
			return res;
		}
		return null;
	},
	/// xóa file attach khỏi hóa đơn
	deleteAttachFile: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/deletefile`,
				params: payload
			},
			true
		);
		return res;
	},

	updateInvAcceptCheckingResult: async function(context, payload) {
		context.commit('updateInvAcceptCheckingResult', payload);
	},

	/**
	 * Lấy thông tin hóa đơn để nhập tay
	 */
	getInvoiceHandEnteredById: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/invoicejson`,
			data: payload
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Lấy nội dung file pdf chưa hỗ trợ xử lý
	 */
	getFilePdfUnsupport: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/pdfunsupport`,
			responseType: 'arraybuffer',
			data: payload
		});
		if (res && res instanceof ArrayBuffer && res.byteLength > 0) {
			return res;
		}
		return null;
	},

	/**
	 * Lấy nội dung file image chưa hỗ trợ xử lý
	 */
	getFileImageUnsupport: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/imageunsupport`,
			responseType: 'arraybuffer',
			data: payload
		});
		if (res && res instanceof ArrayBuffer && res.byteLength > 0) {
			return res;
		}
		return null;
	},

	/**
	 * Lưu nháp hóa đơn
	 */
	saveDraft: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/draft`,
				data: payload
			},
			true
		);
		return res;
	},

	/**
	 * Lưu và đẩy hóa đơn nhập tay để kiểm tra
	 */
	saveInvoiceHanding: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/save/${payload.InvoiceInfo.IsEdit}`,
				data: payload
			},
			true
		);
		return res;
	},

	/**
	 * Lấy hóa đơn từ dùng thử
	 */
	loadCertificateFromTrial: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: CertificateTrialAPI.getAPIUrl() + `/trial` + `/${payload}`
			},
			true
		);
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	getInvoiceNotEnteredZip: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/zipNotEntered`,
			data: payload
		});
		if (res && res.Success) {
			async function download(key) {
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download`,
					responseType: 'arraybuffer'
				});
				if (downloaded instanceof ArrayBuffer) {
					commonFunc.downloadByBlob(downloaded, 'application/zip', 'Hoa_don_chua_nhap.zip');
				} else {
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		}
	},
	getPathFilePdf: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/fileinfospdf',
			params: payload
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	},

	updateInvoiceInfo: async function(context, payload) {
		let res = await httpClient.putAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/pathpdf',
				params: payload
			},
			true
		);
		return res;
	},

	/**
	 * Lấy thông tin file pdf upload
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	getFilePdfNewUpload: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/filepdf`,
				responseType: 'arraybuffer',
				params: payload
			},
			false
		);
		if (res && res instanceof ArrayBuffer) {
			return res;
		}
		return null;
	},

	/**
	 * Thực hiện cập nhật thông tin hạch toán
	 */
	updateAccountingInfo: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/invoiceaccountingdateV2`,
				data: payload
			},
			true
		);
		return res;
	},

	/// Thực hiện cập nhật thông tin hóa đơn thanh toán
	updatePayment: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/invoicepayment`,
				data: payload
			},
			true
		);
		return res;
	},

	/**
	 * Thực hiện cập nhật thông tin hạch toán nhiều hóa đơn
	 */
	updateAccountingInfoMulti: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/invoiceaccountingdatemultiV2`,
				data: payload
			},
			true
		);
		return res;
	},

	//Thực hiện cập nhật ngày hạch toán vào danh sách hóa đơn k cần load lại
	updateAccountDate: async function(context, payload) {
		context.commit('updateAccountDate', payload);
	},

	//Thực hiện cập nhật ghi chú không cần load lại
	updateNote: async function(context, payload) {
		context.commit('updateNote', payload);
	},

	/**
	 * In kết quả kiểm tra
	 */
	printPdfFile: async function(context, payload) {
		const me = this;
		let nameFile = 'ket_qua_kiem_tra';
		let res = await httpClient.getAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + '/print-pdf',
				params: payload,
				responseType: 'arraybuffer'
			},
			true
		);
		if (res) {
			if (res instanceof ArrayBuffer) {
				commonFunc.openPrintResultBlob(res, payload, nameFile);
			}
			return res;
		}
		return null;
	},

	/**
	 * In kết quả kiểm tra
	 */
	printManyPdfFile: async function(context, payload) {
		const me = this;
		let titleProcess = `${i18n.t('i18nCommon.PrintInvoice')}`.toLowerCase();
		let fileName = payload.fileName ? `${payload.fileName}.pdf` : 'danh_sach.pdf';
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		if (payload.fileName) {
			ProgressData.fileName = `${payload.fileName}.pdf`;
		}
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, titleProcess);
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/print-many-pdf`,
			data: payload
		});

		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download-pdf-print`,
					responseType: 'arraybuffer'
				});
				if (downloaded instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, titleProcess);
					commonFunc.openPrintResultBlob(downloaded, payload, fileName);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(
							GlobalEventName.Progress,
							ProgressData,
							`${i18n.t('i18nCommon.PrintInvoice')}`
						);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: 'danh_sach.pdf',
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, `${i18n.t('i18nCommon.PrintInvoice')}`);
			return null;
		}
	},

	/**
	 * In kết quả kiểm tra
	 */
	printCheckingResultPdfFile: async function(context, payload) {
		let titleProcess = `${i18n.t('i18nCommon.PrintCheckingResult')}`.toLowerCase();
		let fileName = payload.fileName ? `${payload.fileName}.pdf` : 'danh_sach.pdf';
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		if (payload.fileName) {
			ProgressData.fileName = `${payload.fileName}.pdf`;
		}
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, titleProcess);
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/print-checking-result`,
			data: payload
		});
		if (res && res.Success) {
			let tryTime = 0;
			async function download(key) {
				tryTime++;
				let downloaded = await httpClient.getAsync({
					url: DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${key}/download-pdf-print`,
					responseType: 'arraybuffer'
				});
				if (downloaded instanceof ArrayBuffer) {
					ProgressData = {
						percentCompleted: 100,
						fileName: fileName,
						Success: true
					};
					EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, titleProcess);
					commonFunc.openByBlob(downloaded, 'application/pdf', `${fileName}`);
				} else {
					if (tryTime >= NumberRetry) {
						ProgressData = {
							percentCompleted: 0,
							fileName: fileName,
							Success: false
						};
						EventBusGlobal.$emit(
							GlobalEventName.Progress,
							ProgressData,
							`${i18n.t('i18nCommon.PrintInvoice')}`
						);
						return;
					}
					setTimeout(() => {
						download(key);
					}, NumberTimeOutRetry);
				}
			}
			setTimeout(() => {
				download(res.Data.Key);
			}, NumberTimeOutRetry);
			return res;
		} else {
			ProgressData = {
				percentCompleted: 0,
				fileName: 'danh_sach.pdf',
				Success: false
			};
			EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData, `${i18n.t('i18nCommon.PrintInvoice')}`);
			return null;
		}
	},

	/**
	 * in số lượng lớn
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	printLargeNumberPdfFile: async function(context, payload) {
		let titleProcess = `${i18n.t('i18nCommon.PrintInvoice')}`.toLowerCase();
		let fileName = payload.fileName ? `${payload.fileName}.pdf` : 'danh_sach.pdf';
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		if (payload.fileName) {
			ProgressData.fileName = `${payload.fileName}.pdf`;
		}
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/print-large-number-pdf`,
			data: payload
		});
		return res;
	},
	/**
	 * Di chuyển hóa đơn
	 */
	moveInvoice: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/move`,
				data: payload
			},
			true
		);
		return res;
	},

	// Lấy tổng hóa đơn - tổng tiền
	getInvTotalCountSum: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/invtotal`,
				params: payload
			},
			true,
			true
		);
		return res;
	},
	// Lấy tổng hóa đơn - tổng tiền
	getInvTotalCountSumV2: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: DashboardAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/v2/invtotal`,
				params: payload
			},
			true,
			true
		);
		return res;
	},

	// Lấy tổng hóa đơn - tổng tiền theo NCC
	getProviderInvTotal: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: DashboardAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/providerinvtotal`,
				params: payload
			},
			true,
			true
		);
		return res;
	},

	// Lấy tổng hóa đơn - tổng tiền theo chart line
	getLineInvTotalCountSum: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: DashboardAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/lineInvtotal`,
				params: payload
			},
			true,
			true
		);
		return res;
	},

	/**
	 * Lấy danh sách kiểm tra thuế suất hóa đơn
	 */
	getCheckingVatInvoice: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/checkingvat/${payload}`
			},
			true
		);
		return res;
	},

	/**
	 * Cập nhật danh sách kiểm tra thuế suất nq43
	 */
	updateVatItemInfo: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/updatevatitem/${payload.InvoiceId}`,
				data: payload.Data
			},
			true
		);
		return res;
	},
	// Thực hiện lấy ra số lượng hóa đơn đến hạn thanh toán
	getinvNumberPaymentWarning: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/paymentWarning/`
		});
		return res;
	},
	/**
	 * Kiểm tra lại file chưa hỗ trợ -  HĐ chưa đọc được
	 */
	checkAgainFileUnsupport: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/checkagainfile`,
			data: payload
		});
		return res;
	},

	getTotalPageAsync: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/total`,
			data: payload
		});
		if (res && res.Success) {
			context.commit('updateTotalPage', res.Data);
		}
		return 0;
	},

	/**
	 * Get tổng số bản ghi theo elastic
	 */
	getTotalPageV3Async: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/totalv3`,
			data: payload
		});
		if (res && res.Success) {
			context.commit('updateTotalPage', res.Data);
		}
		return 0;
	},

	/**
	 * Cập nhật pdf khi nhập tay hóa đơn trùng
	 */
	updatePdfWhenHanding: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/updatepdf`,
				data: payload
			},
			true
		);
		return res;
	},

	/**
	 * Chấp nhận kiểm tra thuế suất hợp lệ
	 */
	acceptTax: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/acceptchecktax`,
				params: payload
			},
			true
		);
		return res;
	},

	/**
	 * Bỏ đánh dấu thanh toán hóa đơn
	 */
	unmarkPaymentInvoice: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/updatepaymentinvoice`,
				data: payload
			},
			true
		);
		return res;
	},

	/**
	 * Tra cứu thông tin tải hóa đơn pdf
	 * @returns
	 */
	searchInvoice: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/search-invoice`,
				data: payload
			},
			true
		);
		if (res && res.Data) {
			context.commit('updateInvoiceSearch', res.Data);
			return res.Data;
		} else {
			context.commit('updateInvoiceSearch', {});
		}
		return null;
	},

	downloadPdfSearchInvoice: async function(context, payload) {
		let fileName = payload.fileName ? `${payload.fileName}.pdf` : 'hoa_don_dau_vao.pdf';
		let ProgressData = {
			percentCompleted: 0,
			fileName: fileName,
			Success: true
		};
		EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
		let res = await httpClient.getAsync({
			url: `${DownloadAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/${payload.InvoiceId}/pdf`,
			responseType: 'arraybuffer',
			onDownloadProgress: function(progressEvent) {
				var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				if (progressEvent.loaded > progressEvent.total) {
					percentCompleted = 100;
				}
				ProgressData = {
					percentCompleted: percentCompleted,
					fileName: fileName,
					Success: true
				};
				EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			}
		});
		if (res) {
			if (res instanceof ArrayBuffer) {
				commonFunc.downloadByBlob(res, 'application/pdf', `${fileName}`);
			} else {
				ProgressData = {
					percentCompleted: 0,
					fileName: `${fileName}`,
					Success: false
				};
				EventBusGlobal.$emit(GlobalEventName.Progress, ProgressData);
			}
			return res;
		}
		return null;
	},

	removePdf: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/removepdf`,
				params: payload
			},
			true
		);
		return res;
	},

	updateStatusViewInvoice: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/viewed`,
			data: payload
		});
		return res;
	},

	updateAcceptedCheckingResult: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/updateacceptcheckingresult`,
				data: payload
			},
			true
		);
		return res;
	},

	translateEmail: async function(context, payload) {
		let res = await httpClient.postAsync(
			{ url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/translate`, data: payload },
			true
		);
		return res;
	},

	getInvoiceInfoXml: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/${payload}/xml`
		});
		if (res && res.Success && res.Data) {
			context.commit('updateInvoiceInfoXml', res.Data);
			return res.Data;
		}
		return null;
	},

	getCostTypes: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/costtypes/${payload}`
		});
		if (res && res.Success && res.Data) {
			context.commit('updateCostTypes', res.Data);
			return res.Data;
		}
		return null;
	},

	updateCostType: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/update-cost-type`,
				data: payload
			},
			true
		);
		if (!res) return null;
		return res;
	},

	updateCostTypeByTime: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/updatecosttypebytime`,
				data: payload
			},
			true
		);
		if (!res) return null;
		return res;
	},
	getCostTypesInvoiceByType: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/costtype/${payload}`
		});
		return res && res.Data;
	},
	getCostTypeByTimeDetails: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/costtypebytime/${payload}`
		});
		return res && res.Data;
	},
	classificationCostInvoice: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/classificationcost`,
				data: payload
			},
			true
		);
		return res && res.Success && res.Data;
	},

	updateCostTypeInvoice: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/updateinvoicecosttype`,
				data: payload
			},
			true
		);
		if (res && res.Data) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Kiểm tra lại tồn tại trên CQT
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	retryCheckExistTaxaxion: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam()) + `/retrycheckexisttaxaxion`,
				data: payload,
				timeout: window.__env.appSetting.timeoutCheckAgain
			},
			true
		);
		return res;
	},
	/**
	 * Cập nhật chi tiết hóa đơn từ cơ quan thuế
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	updateInvoiceDetailFromTaxaxionAgain: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/updateinvdetailfromtaxaxionagainmany`,
				data: payload
			},
			true
		);
		if (res && res.Data) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Lấy tổng tiền chân trang hóa đơn đầu vào
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	getTotalAmountPageAsync: async function(context, payload) {
		//PDDang note: Thực hiện gọi mutiation chung để update loading => (tên mutation: updateLoadingTotalAmountPage, giá trị , root)
		context.commit('updateLoadingTotalAmountPage', true, {
			root: true
		});
		let res = await httpClient.postAsync(
			{ url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/totalamountpage`, data: payload },
			false
		);
		context.commit('updateLoadingTotalAmountPage', false, {
			root: true
		});
		if (res && res.Data) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Lấy tổng tiền chân trang hóa đơn đầu vào elatic search
	 * @param {*} context
	 * @param {*} payload
	 * @returns
	 */
	getTotalAmountPageElasticAsync: async function(context, payload) {
		//PDDang note: Thực hiện gọi mutiation chung để update loading => (tên mutation: updateLoadingTotalAmountPage, giá trị , root)
		context.commit('updateLoadingTotalAmountPage', true, {
			root: true
		});
		let res = await httpClient.postAsync(
			{ url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/totalamountpagev2`, data: payload },
			false
		);
		context.commit('updateLoadingTotalAmountPage', false, {
			root: true
		});
		if (res && res.Data) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Gọi AVA để giải thích thông tin hóa đơn
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Dữ liệu hóa đơn cần giải thích
	 * @returns {Promise<Object|null>} Kết quả giải thích từ AVA hoặc null nếu thất bại
	 * @author dqduy 16.1.2025
	 */
	explainInvoiceInfoByAVAAsync: async function(context, payload) {
		let res = await httpClient.postAsync(
			{ url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/explain-invoice`, data: payload },
			false
		);
		if (res && res.Data) {
			return res.Data;
		}
		return null;
	},

	/**
	 * Ghi logs monitoring khi sử dụng tính năng AVA (gửi lên ELK business để theo dõi)
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Payload chứa JobType để xác định loại action
	 * @param {String} payload.JobType - Loại công việc (CheckBusinessByAva, CostTypeByAva, ExplainInvoiceByAVA...)
	 * @returns {Promise<Boolean|null>} True nếu ghi logs thành công, null nếu thất bại
	 * @author dqduy 16.1.2025
	 */
	monitorAVAAsync: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: `${InvoiceAPI.getAPIUrlSubParam(commonFunc.getSubParam())}/monitor-ava/${payload.JobType}`
		});
		if (res && res.Success) {
			return res.Data;
		}
		return null;
	}
};
